<template>
    <pendo-multiselect
        ref="multiselect"
        multiple
        :trigger-size="triggerSize"
        :full-width="fullWidth"
        :show-selected-values="false"
        :append-to-body="appendToBody"
        :popper-options="popperOptions"
        :clear-on-select="false"
        :close-on-select="false"
        :min-menu-width="268"
        :max-menu-width="400"
        :max-trigger-width="400"
        label-key="displayName"
        group-label-key="group"
        group-options-key="apps"
        :disabled="disabled || hasErrored"
        :loading="isLoadingOptions || loading"
        :placeholder="label"
        :options="options"
        :value="apps"
        :searchable="false"
        class="multi-app-chooser multiselect-filter-apps"
        @open="onOpen"
        @close="onCancel(true)"
        @click-outside="onApply"
        @input="onSelect"
        @leave-lower-bound="onLeaveLowerBound">
        <template #selectedLabel="{ option }">
            <pendo-app-display
                v-if="apps.length === 1 && !explicitAppLabel"
                :apps="option" />
            <span v-else>
                {{ label }}
            </span>
        </template>
        <template
            #trigger
            v-if="useDataMeasureTrigger">
            <pendo-data-measure-trigger />
        </template>
        <template #header="{ comboboxAttrs, handleKeydown, updateInputValue, inputValue }">
            <div class="checkbox-select__header">
                <search
                    v-bind="comboboxAttrs"
                    ref="searchInput"
                    :value="inputValue"
                    autofocus
                    full-width
                    :placeholder="$t('Search apps...')"
                    @input="updateInputValue"
                    @keydown="handleKeydown($event, searhInputKeyOverrides)" />
            </div>
        </template>
        <template #beforeList>
            <slot name="warning" />
            <pendo-multiselect-selected-options
                :selected-options="hasSelectedApps ? apps : []"
                :focus-input-fn="$refs.searchInput?.focusSearchInput"
                @clear="onClear" />
        </template>
        <template #option="{ option, index }">
            <pendo-option-insert
                v-pendo-tooltip="option.tooltip"
                :show-checkbox="!!option.platform"
                :index="index"
                :option="option" />
        </template>
        <template
            #footer="{ handleKeydown }"
            v-if="lazy">
            <div
                ref="footer"
                class="checkbox-select__footer">
                <div>{{ selectionCountLabel }}</div>
                <pendo-button
                    size="mini"
                    type="tertiary"
                    theme="app"
                    :label="$t('Cancel')"
                    @click="onCancel"
                    @keydown="handleKeydown($event, footerKeyOverrides(onCancel))" />
                <pendo-button
                    size="mini"
                    theme="app"
                    :disabled="disableApply"
                    :label="$t('Apply')"
                    @click="onApply"
                    @keydown="handleKeydown($event, footerKeyOverrides(onApply))" />
            </div>
        </template>
    </pendo-multiselect>
</template>

<script>
import {
    PendoButton,
    PendoMultiselect,
    PendoAppDisplay,
    PendoDataMeasureTrigger,
    PendoTooltip,
    PendoOptionInsert,
    PendoMultiselectSelectedOptions,
    PendoMultiselectHelpers,
} from '@pendo/components';
import Search from '@/common/search/search.vue';
import { mapActions } from 'vuex';
import { AppChooserService } from '@/common/app-chooser/app-chooser.service';
import { AppsService } from '@/common/apps/apps.service.js';
import { getAppDisplayName, isPlatformApp, PLATFORM_APPS } from '@/apps/apps.utils';
import cloneDeep from 'lodash/cloneDeep';
import { isEqualArrayContent } from '@/utils/lodash.utils';
import { onLeaveLowerBound } from '@/utils/multiselect-keyboard';
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';
import { trackMultiAppSelectionEvents } from './utils/utils';
const { actionButtonKeyOverride, selectedOptionsKeyOverride, horizontalNavigationKeyOverride, verticalNavigationKeyOverride } = PendoMultiselectHelpers;

export default {
    name: 'MultiAppChooser',
    components: {
        PendoMultiselect,
        PendoMultiselectSelectedOptions,
        PendoButton,
        PendoAppDisplay,
        PendoDataMeasureTrigger,
        PendoOptionInsert,
        Search,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        value: {
            type: Array,
            default: () => [],
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        popperOptions: {
            type: Object,
            default: undefined,
        },
        defaultOptionId: {
            type: String,
            default: PLATFORM_APPS.ALL,
        },
        excludeGroups: {
            type: Boolean,
            default: false,
        },
        transientPlatformOptions: {
            type: Boolean,
            default: false,
        },
        platform: {
            type: [String, Array],
            default: undefined,
        },
        enabledMap: {
            type: Object,
            default: () => ({}),
        },
        filterNonIntegratedApps: {
            type: Boolean,
            default: false,
        },
        explicitAppLabel: {
            type: Boolean,
            default: false,
        },
        allowEmpty: {
            type: Boolean,
            default: false,
        },
        useDataMeasureTrigger: {
            type: Boolean,
            default: false,
        },
        includeComboWebAndExtensionOption: {
            type: Boolean,
            default: false,
        },
        excludePartnerAdoptApp: {
            type: Boolean,
            default: false,
        },
        sessionReplay: {
            type: Boolean,
            default: false,
        },
        includeAllAppsOption: {
            type: Boolean,
            default: false,
        },
        triggerSize: {
            type: String,
            default: 'medium',
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        lazy: {
            type: Boolean,
            default: true,
        },
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        disableMobileOption: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['loadError', 'apply'],
    setup () {
        const { queueMessage } = useAriaLiveRegistry();

        return { queueMessage };
    },
    data () {
        return {
            isLoadingOptions: true,
            hasErrored: false,
            apps: null,
        };
    },
    computed: {
        label () {
            const len = this.apps?.length;

            if (!len && (this.allowEmpty || this.transientPlatformOptions)) {
                return this.$t('Select apps');
            }

            if (!len) {
                return this.$t('All apps ({totalAppsCount})', { totalAppsCount: this.totalAppsCount });
            }

            if (len === 1) {
                if (isPlatformApp(this.apps[0].id)) {
                    return `${this.apps[0].displayName} (${this.selectedAppsCount})`;
                }

                return this.apps[0].displayName;
            }

            if (this.explicitAppLabel) {
                return `${this.apps[0].displayName}, +${len - 1}`;
            }

            return AppsService.getAppGroupLabelBySelection(this.individualAppOptions, this.apps);
        },
        selectionCountLabel () {
            return this.$t('{selectedAppsCount} of {totalAppsCount} selected', {
                selectedAppsCount: this.selectedAppsCount,
                totalAppsCount: this.totalAppsCount,
            });
        },
        options () {
            return AppChooserService.formatOptions(
                this.platform,
                this.excludeGroups,
                this.enabledMap,
                this.filterNonIntegratedApps,
                {
                    includeComboWebAndExtensionOption: this.includeComboWebAndExtensionOption,
                    excludePartnerAdoptApp: this.excludePartnerAdoptApp,
                    includeAllAppsOption: this.includeAllAppsOption,
                    sessionReplay: this.sessionReplay,
                    groupLabelType: this.transientPlatformOptions ? 'verb' : 'noun',
                    disableMobileOption: this.disableMobileOption,
                },
            );
        },
        individualAppOptions () {
            const appsIndex = this.excludeGroups ? 0 : 1;

            return this.options[appsIndex]?.apps || [];
        },
        selectedAppsCount () {
            if (!this.apps?.length) {
                return 0;
            }

            const firstAppId = this.apps[0].id;
            if (isPlatformApp(firstAppId)) {
                const platformAggId = `expandAppIds("${firstAppId}")`;

                return AppsService.expandAppIds(platformAggId, this.individualAppOptions).length;
            }

            return this.apps.length;
        },
        totalAppsCount () {
            return this.individualAppOptions.length;
        },
        hasSelectedApps () {
            return !!(this.apps?.length && !isPlatformApp(this.apps?.[0].id));
        },
        disableApply () {
            return !this.allowEmpty && !this.apps?.length;
        },
        searhInputKeyOverrides () {
            if (this.hasSelectedApps) {
                return selectedOptionsKeyOverride(this.apps, this.$refs.multiselect);
            }

            return {};
        },
    },
    watch: {
        value: {
            immediate: true,
            deep: true,
            handler (apps) {
                this.apps = cloneDeep(this.populateAppInfo(apps));
            },
        },
    },
    async created () {
        try {
            await this.fetchApps();
        } catch (error) {
            this.$emit('loadError', error);
            this.hasErrored = true;
        }

        this.isLoadingOptions = false;
    },
    methods: {
        ...mapActions({
            fetchApps: 'apps/loadAll',
        }),
        footerKeyOverrides (actionFn) {
            return {
                ...actionButtonKeyOverride(actionFn),
                ...horizontalNavigationKeyOverride(this.$refs.footer),
                ...verticalNavigationKeyOverride(this.$refs.searchInput.focusSearchInput),
                Home: this.$refs.searchInput.focusSearchInput,
            };
        },
        onSelect (selectedApps) {
            const appIds = selectedApps?.map(({ id }) => id);

            if (!selectedApps?.length) {
                if (this.transientPlatformOptions || (this.allowEmpty && this.excludeGroups)) {
                    this.apps = [];
                } else if (isPlatformApp(this.apps?.[0].id)) {
                    this.onApply();
                } else {
                    this.apps = [this.getDefaultOption()];
                }
            } else {
                // if previous option was platform option, remove it from selected apps.
                if (appIds.length > 1 && isPlatformApp(appIds[0])) {
                    selectedApps.shift();
                }

                // if platform option is selected, remove all other selections
                if (isPlatformApp(appIds[appIds.length - 1])) {
                    if (this.transientPlatformOptions) {
                        const platformAggId = `expandAppIds("${appIds[appIds.length - 1]}")`;
                        const filteredIds = AppsService.expandAppIds(platformAggId, this.individualAppOptions);

                        this.apps = this.individualAppOptions.filter(({ id }) => filteredIds.includes(id));
                    } else {
                        this.apps = [selectedApps[selectedApps.length - 1]];
                        this.onApply();
                    }
                } else {
                    this.apps = selectedApps;
                }
            }

            this.queueMessage({ message: this.selectionCountLabel });

            if (!this.lazy) {
                this.onApply();
            }
        },
        onClear () {
            let additionalContext = '';
            if (this.transientPlatformOptions || (this.allowEmpty && this.excludeGroups)) {
                this.apps = [];
            } else {
                this.apps = [this.getDefaultOption()];
                additionalContext = this.$t('Reverted to default selection: {default}.', { default: this.apps[0].displayName });
            }

            this.queueMessage({ message: additionalContext + this.selectionCountLabel });

            if (!this.lazy) {
                this.onApply();
            }
        },
        onCancel (isClosing = false) {
            this.apps = cloneDeep(this.populateAppInfo(this.value));
            if (!isClosing) {
                this.$refs.multiselect.toggleMenu();
            }
        },
        onApply () {
            const newAppIds = this.apps.map(({ id }) => id);
            const oldAppIds = this.value.map(({ id }) => id);

            if (!this.allowEmpty && !newAppIds.length) {
                this.onCancel();

                return;
            }

            if (!isEqualArrayContent(newAppIds, oldAppIds)) {
                this.$emit('apply', newAppIds);
                trackMultiAppSelectionEvents(this.apps, this.$router?.currentRoute?.value.name);
            }

            if (this.closeOnSelect) {
                this.$refs.multiselect.toggleMenu();
            }
        },
        onLeaveLowerBound ({ availableOptions }) {
            onLeaveLowerBound(this.$refs.multiselect, availableOptions.length, this.$refs.footer);
        },
        getDefaultOption () {
            return this.options
                .flatMap((option) => option.apps)
                .find((app) => app.id === this.defaultOptionId);
        },
        populateAppInfo (list) {
            if (!list.length) return list;

            // Transform raw IDs into option objects
            if (list.every((id) => typeof id === 'number' || typeof id === 'string')) {
                if (isPlatformApp(list[0])) {
                    const platformApps = cloneDeep(this.options[0].apps);
                    const nonAggAppId = AppsService.getOptionIdFromAggId(list[0]);

                    list = platformApps.filter((option) => nonAggAppId === option.id);
                } else {
                    const ids = list.map((id) => parseInt(id));

                    list = this.individualAppOptions.filter((option) => ids.includes(option.id));
                }
            }

            // Transform platform options to collections of app options
            if (this.transientPlatformOptions && getAppDisplayName(list[0].id)) {
                const platformAggId = `expandAppIds("${list[0].id}")`;
                const appIds = AppsService.expandAppIds(platformAggId, this.individualAppOptions);

                list = this.individualAppOptions.filter(({ id }) => appIds.includes(id));
            }

            return list;
        },
        /**
         * @public
         */
        toggleMenu () {
            this.$refs.multiselect.toggleMenu();
        },
    },
};
</script>

<style lang="less" scoped>
.checkbox-select__header {
    padding: 16px;
    border-bottom: 1px solid var(--palette-gray-30);
}

.checkbox-select__footer {
    color: var(--palette-gray-80);
    background-color: var(--palette-gray-10);
    border-top: 1px solid var(--palette-gray-30);
    display: grid;
    grid-template-columns: 1fr auto auto;
    grid-gap: 8px;
    align-items: center;
    padding: 8px;
    font-size: 12.642px;
}
</style>
